import {z as zod} from "zod";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {
    datasetMetadataZodValidator,
    datasetZodValidator,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/a/_adminScaffold/debugger/-types";

type AnalyticsContext = {
    intellsysBaseUrl: string;
    accessToken: string;
};

// Erato only grants access to healthy datasets. Its analytics metadata omits
// healthStatus, which the existing report editor's dataset model requires.
function reportDatasetValidator<Response>(validator: zod.ZodType<Response>) {
    return zod.preprocess((value) => {
        if (value === null || typeof value !== "object" || Array.isArray(value)) {
            return value;
        }
        return {...value, healthStatus: "533636e9-abd0-49ae-97b5-16756a48b62e"};
    }, validator);
}

function postAnalytics<Response>(
    context: AnalyticsContext,
    path: string,
    request: Record<string, unknown>,
    validator: zod.ZodType<Response>,
) {
    return robustJsonResultClientFetch(
        `${context.intellsysBaseUrl}/sub-system/erato/analytics/${path}`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${context.accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(request),
        },
        validator,
    );
}

export function fetchDatasets(context: AnalyticsContext & {workspaceId: Uuid}) {
    return postAnalytics(
        context,
        "datasets",
        {workspaceId: context.workspaceId},
        reportDatasetValidator(datasetMetadataZodValidator).array(),
    );
}

export function fetchDatasetDetails(context: AnalyticsContext & {workspaceId: Uuid; datasetId: Uuid}) {
    return postAnalytics(
        context,
        "dataset-details",
        {workspaceId: context.workspaceId, datasetId: context.datasetId},
        reportDatasetValidator(datasetZodValidator),
    );
}

export function fetchDistinctValues(context: AnalyticsContext & {
    request: {workspaceId: Uuid; datasetId: Uuid; attributeId: Uuid; limit?: number};
}) {
    return postAnalytics(
        context,
        "dataset/distinct-values",
        {...context.request, limit: context.request.limit ?? 100},
        zod.array(zod.union([zod.string(), zod.number(), zod.boolean(), zod.null()])),
    );
}
